import type { CartLine } from '../lib/cart';
import { formatMoneyCents } from '../lib/format';
import { useListMotion } from '../lib/motion/useListMotion';

type ChargeCartProps = {
  lines: CartLine[];
  /** Quantity steppers; a next quantity of cero removes the Línea (docs/FLOWS.md §1). */
  onSetQuantity: (lineId: string, quantity: number) => void;
  subtotalCents: number;
  discountCents: number;
  /** e.g. "10%" or "$5.00", shown next to the discount line; absent when there is no discount. */
  discountLabel?: string;
  totalCents: number;
  /** True on the Confirmar step: the cart is shown but no longer editable (total locked). */
  readOnly?: boolean;
  /** Rendered between the Líneas and the totals block (the Descuento control lives here). */
  beforeTotals?: React.ReactNode;
  emptyHint?: string;
};

/** Every Línea with its stepper, then subtotal/descuento/Total. The pane provides the card. */
export function ChargeCart({
  lines,
  onSetQuantity,
  subtotalCents,
  discountCents,
  discountLabel,
  totalCents,
  readOnly = false,
  beforeTotals,
  emptyHint = 'Toca un servicio para empezar la cuenta.',
}: ChargeCartProps) {
  const lineMotion = useListMotion<HTMLUListElement>();
  if (lines.length === 0) {
    return (
      <p className="pos-empty">
        Todavía no agregaste nada.
        <span className="hint">{emptyHint}</span>
      </p>
    );
  }

  return (
    <div>
      <ul className="cart-lines" ref={lineMotion}>
        {lines.map((line) => (
          <li key={line.id} className="cart-line">
            <div>
              <p className="cart-line-name">{line.name}</p>
              <p className="cart-line-unit">
                {formatMoneyCents(line.unitPriceCents)}
                {line.quantity > 1 ? ' c/u' : ''}
              </p>
            </div>
            {readOnly ? (
              <span className="cart-line-unit">× {line.quantity}</span>
            ) : (
              <span className="stepper">
                <button
                  type="button"
                  aria-label={`Quitar una unidad de ${line.name}`}
                  onClick={() => onSetQuantity(line.id, line.quantity - 1)}
                >
                  −
                </button>
                <span className="qty">{line.quantity}</span>
                <button
                  type="button"
                  aria-label={`Agregar una unidad de ${line.name}`}
                  onClick={() => onSetQuantity(line.id, line.quantity + 1)}
                >
                  +
                </button>
              </span>
            )}
            <p className="amount">{formatMoneyCents(line.unitPriceCents * line.quantity)}</p>
          </li>
        ))}
      </ul>
      {beforeTotals}
      <div className="pos-totals">
        <p className="line">
          Subtotal <span className="amount">{formatMoneyCents(subtotalCents)}</span>
        </p>
        {discountCents > 0 ? (
          <p className="line">
            Descuento{discountLabel ? ` (${discountLabel})` : ''}{' '}
            <span className="amount">−{formatMoneyCents(discountCents)}</span>
          </p>
        ) : null}
        <p className="line total">
          Total <span className="amount">{formatMoneyCents(totalCents)}</span>
        </p>
      </div>
    </div>
  );
}
